import React, { useState, useRef, useEffect } from 'react';

import { IconButton } from '@atlaskit/button/new';
import ChevronLeftIcon from '@atlaskit/icon/utility/migration/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { Flex, Grid } from '@atlaskit/primitives';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { HTV2ActionCard } from './HTV2ActionCard';
import type { OnboardingAction } from './useOnboardingActions';

function useCarousel<I>(items: I[], pageSize: number) {
	const [idx, setIdx] = useState(0);
	const hasNext = idx + pageSize < items.length;
	const hasPrevious = idx > 0;
	return {
		next: hasNext ? () => setIdx(idx + 1) : undefined,
		previous: hasPrevious ? () => setIdx(idx - 1) : undefined,
		visible: items.slice(idx, idx + pageSize),
	};
}

/**
 * Custom hook that checks the homeIsSingleColumn CSS variable.
 *
 * @returns {Object} An object containing:
 * - `ref`: A ref that must be attached to an element that has the
 *   `--homeIsSingleColumn` CSS variable. It should be an element that spans
 *   the width of the home content area, since we will watch its width to
 *   decide when to update.
 * - `isSingleColumn`: A boolean indicating the value of the
 *   `--homeIsSingleColumn` CSS variable (true if '1', false otherwise).
 */
function useHomeIsSingleColumn() {
	const ref = useRef<HTMLElement>(null);
	const [isSingleColumn, setIsSingleColumn] = useState(false);

	useEffect(() => {
		if (!ref.current) {
			return;
		}
		// We watch the carousel for size changes (via the ref), as it's possible that --homeIsSingleColumn
		// can change without a re-render (e.g. if the user resizes the window or the sidebar).
		const resizeObserver = new ResizeObserver(() => {
			setIsSingleColumn(
				Boolean(
					ref.current &&
						window.getComputedStyle(ref.current).getPropertyValue('--homeIsSingleColumn') === '1',
				),
			);
		});
		resizeObserver.observe(ref.current);
		return () => resizeObserver.disconnect();
	}, []);

	return {
		ref,
		isSingleColumn,
	};
}

export function HTV2Carousel({ actions }: { actions: OnboardingAction[] }) {
	const { ref, isSingleColumn } = useHomeIsSingleColumn();
	const numVisible = isSingleColumn ? 2 : 3;
	const { next, previous, visible } = useCarousel(actions, numVisible);
	const showControls = actions.length > numVisible;
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const withAnalytics = (callback: (() => void) | undefined) => {
		if (!callback) {
			return;
		}

		return () => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'homeOnboardingTakeoverV2ActionCarouselScroll',
					source: 'onboarding',
				},
			}).fire();
			callback();
		};
	};

	return (
		<Flex ref={ref} direction="column" gap="space.100">
			<Grid gap="space.200" templateColumns="repeat(calc(3 - var(--homeIsSingleColumn)), 1fr)">
				{visible.map((action) => (
					<HTV2ActionCard key={action.id} action={action} />
				))}
			</Grid>

			{showControls && (
				<Flex direction="row" justifyContent="center" gap="space.100">
					<IconButton
						label="show previous item"
						icon={ChevronLeftIcon}
						onClick={withAnalytics(previous)}
						isDisabled={!previous}
					/>
					<IconButton
						label="show next item"
						icon={ChevronRightIcon}
						onClick={withAnalytics(next)}
						isDisabled={!next}
					/>
				</Flex>
			)}
		</Flex>
	);
}
